import {
  ClickwrapAcceptedAgreementRequest,
  ClickwrapEvent,
  ClickwrapSignatureResponse,
  ClickwrapTemplateContent,
  ClickwrapTemplateRequest,
  ClickwrapTemplateResponse,
  ClickwrapCustomizationSettingsResponse,
} from "./models";
import { normalizeTemplatePlaceholders } from "./utils";

declare const __CLICKTERM_SDK_VERSION__: string;

const SDK_HEADERS: Record<string, string> = {
  'X-Clickterm-SDK-Type': 'WEB',
  'X-Clickterm-SDK-Version': __CLICKTERM_SDK_VERSION__,
};
export class HttpService {
  private readonly headers: Record<string, string>;

  constructor(
    private readonly baseUrl: string,
    headers: Record<string, string>,
    private readonly queryParams: Record<string, string>
  ) {
    this.headers = { ...SDK_HEADERS, ...headers };
  }

  async clickwrap(request: ClickwrapTemplateRequest): Promise<ClickwrapTemplateResponse> {
    const language = request.language ?? 'en';
    const queryParams = this.createQueryParams()

    const clickwrapRequestBody: ClickwrapTemplateRequest = {
        clickwrapTemplateId: request.clickwrapTemplateId,
        endUserId: request.endUserId,
        language,
        interactionType: request.interactionType,
        templatePlaceholders: normalizeTemplatePlaceholders(request.templatePlaceholders),
    }


    const response = await fetch(`${this.baseUrl}/public-client/v1/clickwrap/request?${queryParams}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.headers,
      },
      body: JSON.stringify(clickwrapRequestBody),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new HttpError(response.status, text);
    }
    if (response.status === 204) {
      return { clicktermSignature: null, content: null };
    }
    return response.json();
  }

  async getAcceptedContent(request: ClickwrapAcceptedAgreementRequest): Promise<ClickwrapTemplateContent> {
    const queryString = this.createQueryParams({
      endUserId: request.endUserId,
      clickwrapTemplateId: request.clickwrapTemplateId,
    }).toString();
    const response = await fetch(`${this.baseUrl}/public-client/v1/clickwrap/content?${queryString}`, {
      method: 'GET',
      headers: this.headers,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new HttpError(response.status, text);
    }
    if (response.status === 204) {
      throw new HttpError(response.status, 'Empty response when fetching accepted content. Maybe the agreement was not accepted.');
    }
    return response.json();
  }

  async getCustomizations(clickwrapTemplateId: string): Promise<ClickwrapCustomizationSettingsResponse | null> {
    const queryString = this.createQueryParams({
      clickwrapTemplateId,
    }).toString();
    const response = await fetch(`${this.baseUrl}/public-client/v1/clickwrap/customizations?${queryString}`, {
      method: 'GET',
      headers: this.headers,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new HttpError(response.status, text);
    }
    if (response.status === 204) {
      return null;
    }
    return response.json();
  }

  async applyAgreement(request: ClickwrapEvent): Promise<ClickwrapSignatureResponse> {
    const headers = {
      'Content-Type': 'application/json',
      ...this.headers
    };
    const queryString = this.createQueryParams().toString();
    const response = await fetch(`${this.baseUrl}/public-client/v1/clickwrap?${queryString}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        clickwrapTemplateId: request.clickwrapTemplateId,
        clickwrapTemplateVersion: request.clickwrapTemplateVersion,
        clickwrapTemplateVersionMinor: request.clickwrapTemplateVersionMinor,
        endUserId: request.endUserId,
        clickwrapEventStatus: request.clickwrapEventStatus,
        ...(request.interactionType ? { interactionType: request.interactionType } : {}),
      }),
    });
    if (!response.ok) {
      return response.text().then(text => {
        throw new HttpError(response.status, text);
      });
    }
    return response.json();
  }

  private createQueryParams(params: Record<string, string> = {}): URLSearchParams {
    return new URLSearchParams({...this.queryParams, ...params});
  }
}

class HttpError extends Error {
  constructor(public status: number, public message: string) {
    super(message);
  }
}
